/**
 * Twenty of the addresses this campaign would actually write to.
 *
 * Counts can be right and still describe the wrong people, and the fastest way
 * to notice that is to read a handful of the names out loud. So the sample is
 * not decoration: it is the check no aggregate can perform.
 *
 * Two flags ride along because they are judgement calls the screening
 * deliberately does not make on its own - a consumer mailbox weakens the
 * business footing of a cold message, and a role account is the company's own
 * published channel and therefore the safest target rather than the worst one.
 * Both are marked and neither is removed.
 */
import { useT } from "../../../../../i18n/I18nProvider";
import type { AudiencePreview } from "../../../../../lib/marketing";

export function SampleList({ sample }: { sample: AudiencePreview["sample"] }) {
  const t = useT();
  if (sample.length === 0) return null;

  return (
    <div className="space-y-2">
      <h4 className="text-xs font-semibold">{t("marketing.preview.sample")}</h4>
      <ul className="divide-y divide-border/60 overflow-hidden rounded-xl border border-border">
        {sample.map((s) => (
          <li key={`${s.ref}-${s.email}`} className="px-3 py-2">
            <div className="flex items-baseline justify-between gap-2">
              <span className="min-w-0 truncate text-xs font-medium">{s.companyName ?? s.ref}</span>
              <span className="flex shrink-0 items-center gap-1">
                {s.freemail && (
                  <span className="rounded bg-amber-500/15 px-1 py-px text-[9px] uppercase tracking-wide text-amber-700 dark:text-amber-400">
                    {t("marketing.preview.freemailTag")}
                  </span>
                )}
                {s.role && (
                  <span className="rounded bg-muted px-1 py-px text-[9px] uppercase tracking-wide text-muted-foreground">
                    {t("marketing.preview.role")}
                  </span>
                )}
              </span>
            </div>
            <div className="mt-0.5 truncate font-mono text-[11px] text-muted-foreground">{s.email}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}
